import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const WorkspaceTabsContext = createContext(null);
const STORAGE_KEY = 'gamesfinance.workspaceTabs.v1';
const ACTIVE_STORAGE_KEY = 'gamesfinance.activeWorkspaceTab.v1';
const MAX_TABS = 12;

export const getWorkspaceTabId = (path) => path || '/';

const readStoredTabs = () => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(tab => tab?.path && tab?.title)
      .map(tab => ({
        ...tab,
        id: getWorkspaceTabId(tab.path),
        isDirty: false
      }));
  } catch (error) {
    return [];
  }
};

const buildWorkspaceTab = ({ path, title, subtitle = '', kind = 'page' }) => ({
  id: getWorkspaceTabId(path),
  path,
  title,
  subtitle,
  kind,
  isDirty: false
});

export const WorkspaceTabsProvider = ({ children }) => {
  const [tabs, setTabs] = useState(readStoredTabs);
  const [activeTabId, setActiveTabId] = useState(() => {
    try {
      return window.sessionStorage.getItem(ACTIVE_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  });

  useEffect(() => {
    try {
      const storedTabs = tabs.map(({ isDirty, ...tab }) => tab);
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storedTabs));
    } catch (error) {
      // Session persistence is a convenience; tab state still works without it.
    }
  }, [tabs]);

  useEffect(() => {
    try {
      if (activeTabId) {
        window.sessionStorage.setItem(ACTIVE_STORAGE_KEY, activeTabId);
      } else {
        window.sessionStorage.removeItem(ACTIVE_STORAGE_KEY);
      }
    } catch (error) {
      // Ignore storage failures.
    }
  }, [activeTabId]);

  const openWorkspaceTab = useCallback(({ path, title, subtitle = '', kind = 'page' }) => {
    if (!path) return null;

    const nextTab = buildWorkspaceTab({ path, title, subtitle, kind });
    setTabs(prevTabs => {
      const existingIndex = prevTabs.findIndex(tab => tab.id === nextTab.id);
      if (existingIndex >= 0) {
        return prevTabs.map((tab, index) => (
          index === existingIndex
            ? {
                ...tab,
                title: nextTab.title || tab.title,
                subtitle: nextTab.subtitle || tab.subtitle,
                kind: nextTab.kind || tab.kind
              }
            : tab
        ));
      }

      return [...prevTabs, nextTab].slice(-MAX_TABS);
    });
    setActiveTabId(nextTab.id);

    return nextTab;
  }, []);

  const activateWorkspaceTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  const closeWorkspaceTab = useCallback((tabId) => {
    setTabs(prevTabs => prevTabs.filter(tab => tab.id !== tabId));
    setActiveTabId(prevActive => (prevActive === tabId ? '' : prevActive));
  }, []);

  const updateWorkspaceTab = useCallback((tabId, patch) => {
    setTabs(prevTabs => prevTabs.map(tab => (
      tab.id === tabId ? { ...tab, ...patch } : tab
    )));
  }, []);

  const reorderWorkspaceTabs = useCallback((sourceTabId, targetTabId) => {
    if (!sourceTabId || !targetTabId || sourceTabId === targetTabId) return;

    setTabs(prevTabs => {
      const sourceIndex = prevTabs.findIndex(tab => tab.id === sourceTabId);
      const targetIndex = prevTabs.findIndex(tab => tab.id === targetTabId);
      if (sourceIndex < 0 || targetIndex < 0) return prevTabs;

      const nextTabs = [...prevTabs];
      const [movedTab] = nextTabs.splice(sourceIndex, 1);
      nextTabs.splice(targetIndex, 0, movedTab);
      return nextTabs;
    });
  }, []);

  const value = useMemo(() => ({
    tabs,
    activeTabId,
    openWorkspaceTab,
    activateWorkspaceTab,
    closeWorkspaceTab,
    updateWorkspaceTab,
    reorderWorkspaceTabs
  }), [tabs, activeTabId, openWorkspaceTab, activateWorkspaceTab, closeWorkspaceTab, updateWorkspaceTab, reorderWorkspaceTabs]);

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
};

export const useWorkspaceTabs = () => {
  const context = useContext(WorkspaceTabsContext);
  if (!context) {
    throw new Error('useWorkspaceTabs must be used within WorkspaceTabsProvider');
  }
  return context;
};

export const useWorkspaceTabNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { closeWorkspaceTab } = useWorkspaceTabs();
  const currentWorkspacePath = `${location.pathname}${location.search}${location.hash}`;

  const closeCurrentTabAndNavigate = useCallback((to, options) => {
    closeWorkspaceTab(currentWorkspacePath);
    navigate(to, options);
  }, [closeWorkspaceTab, currentWorkspacePath, navigate]);

  return { closeCurrentTabAndNavigate };
};
